// src/App.js
import React, { useContext, useState, useEffect } from 'react';
import Layout from './components/Layout/Layout';
import LandingPage from './components/LandingPage/LandingPage';
import CharacterDashboard from './components/CharacterDashboard/CharacterDashboard';
import { MessageLogProvider } from './contexts/MessageLogContext';
import { AuthProvider, AuthContext } from './contexts/AuthContext';

function AppContent() {
  const { currentUser, authLoading, isGuest, isLoadingCharacters, fetchUserCharacters } = useContext(AuthContext);
  const [selectedCharacterData, setSelectedCharacterData] = useState(null);
  const [view, setView] = useState('loading'); // 'loading', 'landing', 'dashboard', 'game'

  useEffect(() => {
    if (authLoading || (currentUser && !isGuest && isLoadingCharacters)) { // Keep loading if characters are loading for a signed-in user
      setView('loading');
    } else if (!currentUser && !isGuest) {
      setView('landing');
      setSelectedCharacterData(null); 
    } else if (currentUser && !isGuest && !selectedCharacterData) {
      // If dashboard is the target and characters might be stale, ensure they are fetched.
      // This might be redundant if onAuthStateChanged already handles it, but can be a safeguard.
      // fetchUserCharacters(currentUser.uid); // Consider if this is needed or if onAuthStateChanged is sufficient
      setView('dashboard');
    } else if ((currentUser && selectedCharacterData) || isGuest) {
      setView('game');
    } else {
      // Fallback or default view if logic gets complex
      setView('landing');
    }
  }, [currentUser, authLoading, isGuest, selectedCharacterData, isLoadingCharacters]);

  const handleCharacterSelect = (characterDataWithFullPlayerState) => {
    console.log("[AppContent] Character selected/created:", characterDataWithFullPlayerState);
    setSelectedCharacterData(characterDataWithFullPlayerState);
    setView('game');
  };

  const handleReturnToDashboard = async () => {
    setSelectedCharacterData(null); // Clear selected character to stop game layout
    if (currentUser && !isGuest) {
      console.log("[AppContent] Returning to dashboard, fetching fresh characters...");
      await fetchUserCharacters(currentUser.uid); // Fetch fresh character data
    }
    setView('dashboard'); // Then switch to dashboard view
  };

  if (view === 'loading') {
    return <div style={{color: "white", textAlign: "center", paddingTop: "50px", fontSize: "1.2em"}}>Loading Application...</div>;
  }

  if (view === 'landing') {
    return <LandingPage />;
  }

  if (view === 'dashboard') {
    // CharacterDashboard internally uses userCharacters from AuthContext, which should be updated by fetchUserCharacters
    return <CharacterDashboard onCharacterSelect={handleCharacterSelect} />;
  }

  if (view === 'game') {
    return (
      <Layout
        initialPlayerData={selectedCharacterData} 
        isGuestPlayer={isGuest}
        onReturnToDashboard={currentUser && !isGuest ? handleReturnToDashboard : null}
      />
    );
  }

  return <LandingPage />; // Default fallback
}

function App() {
  return (
    <AuthProvider>
      <MessageLogProvider>
        <AppContent />
      </MessageLogProvider>
    </AuthProvider>
  );
}

export default App;
